import { Logger } from '@vimeo/logging';

/* eslint-disable @typescript-eslint/no-explicit-any */
type TEventMap = Record<string, Array<any>>;
type TListener<T extends Array<any>> = (...args: T) => void;
/* eslint-enable @typescript-eslint/no-explicit-any */

export class EventEmitter<Events extends TEventMap = TEventMap> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly events: Map<keyof Events, Array<TListener<any>>> = new Map();

  public on<K extends keyof Events>(eventName: K, listener: TListener<Events[K]>): void {
    if (!this.events.has(eventName)) {
      this.events.set(eventName, []);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.events.get(eventName) as Array<TListener<any>>).push(listener);
  }

  public once<K extends keyof Events>(eventName: K, listener: TListener<Events[K]>): void {
    const onceWrapper: TListener<Events[K]> = (...args) => {
      this.off(eventName, onceWrapper);
      listener(...args);
    };
    // we attach the original listener to the wrapper so that `off` can find it if needed
    // (e.g., emitter.off('event', originalListener) before it fires once)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
    (onceWrapper as any).originalListener = listener;
    this.on(eventName, onceWrapper);
  }

  public off<K extends keyof Events>(eventName: K, listener: TListener<Events[K]>): void {
    const listeners = this.events.get(eventName);
    if (!listeners) {
      return;
    }
    // checking originalListener is required for 'once' events
    const updatedListeners = listeners.filter(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
      (l) => l !== listener && (l as any).originalListener !== listener,
    );
    if (updatedListeners.length > 0) {
      this.events.set(eventName, updatedListeners);
    } else {
      this.events.delete(eventName);
    }
  }

  public emit<K extends keyof Events>(eventName: K, ...args: Events[K]): void {
    const listeners = this.events.get(eventName);
    if (!listeners || listeners.length === 0) {
      return;
    }
    // use a copy of the array `[...listeners]` in case a listener
    // modifies the original array (e.g., by calling `off`) during iteration
    [...listeners].forEach((listener) => {
      try {
        listener(...args);
      } catch (err) {
        Logger.getGlobal().error(`event handler for "${String(eventName)}" failed`, err);
      }
    });
  }

  public removeAllListeners(eventName?: keyof Events): void {
    if (eventName) {
      this.events.delete(eventName);
    } else {
      this.events.clear();
    }
  }
}
